<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PageHeader from 'dashboard/components-next/PageHeader.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  headerSubtitle: { type: String, default: '' },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <div class="sticky top-0 z-20 px-4 md:px-6 bg-n-surface-1">
    <PageHeader
      :title="headerTitle"
      :subtitle="headerSubtitle"
      class="pt-6 pb-4 mx-auto max-w-7xl"
    >
      <template #actions>
        <Button
          :label="buttonLabel"
          icon="i-lucide-plus"
          size="sm"
          @click="emit('add')"
        />
      </template>
      <template #toolbar>
        <div class="flex flex-wrap items-center w-full gap-2">
          <div v-if="showSearch" class="w-full sm:w-72">
            <Input
              :model-value="searchValue"
              type="search"
              :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
              :custom-input-class="[
                'h-9 bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
              ]"
              class="w-full"
              @input="emit('search', $event.target.value)"
            >
              <template #prefix>
                <Icon
                  icon="i-lucide-search"
                  class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
                />
              </template>
            </Input>
          </div>

          <div v-if="!isLabelView && !isActiveView" class="relative">
            <Button
              id="toggleContactsFilterButton"
              :label="
                isSegmentsView
                  ? $t('CONTACTS_LAYOUT.FILTER.EDIT_SEGMENT')
                  : $t('CONTACTS_LAYOUT.FILTER.BUTTONS.ADD_FILTER')
              "
              :icon="
                isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
              "
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('filter')"
            />
            <span
              v-if="hasActiveFilters && !isSegmentsView"
              class="absolute w-2 h-2 rounded-full pointer-events-none -top-0.5 ltr:-right-0.5 rtl:-left-0.5 bg-n-brand"
            />
            <slot name="filter" />
          </div>

          <div class="flex items-center gap-2 ltr:ml-auto rtl:mr-auto">
            <Button
              v-if="
                hasActiveFilters &&
                !isSegmentsView &&
                !isLabelView &&
                !isActiveView
              "
              v-tooltip.bottom="
                $t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.TITLE'
                )
              "
              icon="i-lucide-save"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('createSegment')"
            />
            <Button
              v-if="isSegmentsView && !isLabelView && !isActiveView"
              v-tooltip.bottom="
                $t(
                  'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.DELETE_SEGMENT.TITLE'
                )
              "
              icon="i-lucide-trash"
              color="slate"
              size="sm"
              variant="ghost"
              @click="emit('deleteSegment')"
            />
            <ContactSortMenu
              :active-sort="activeSort"
              :active-ordering="activeOrdering"
              @update:sort="emit('update:sort', $event)"
            />
            <ContactMoreActions
              @add="emit('add')"
              @import="emit('import')"
              @export="emit('export')"
            />
          </div>
        </div>
      </template>
    </PageHeader>
  </div>
</template>
